import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import DcPage from "@/components/landing/dcv2/DcPage";
import {
  CheckCircle2, Clock, Mail, Briefcase, MapPin, User, FileText,
  ArrowRight, Home, Phone, DollarSign,
} from "lucide-react";
import {
  formatInstantInTimezone,
  formatInstantTo12Hour,
  getDisplayTimezone,
} from "@/utils/timezone";
import { glassCardStyle, surfaceStyle } from "@/components/auth/authStyles";

interface SubmissionState {
  job: {
    title: string;
    location: string;
    employmentType: string;
    workplaceType: string;
    seniorityLevel: string;
    salaryRange?: string;
  };
  applicant: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    resumeFileName?: string;
  };
}

const LABEL_MAPS: Record<string, Record<string, string>> = {
  employmentType: {
    full_time: "Full Time", part_time: "Part Time", contract: "Contract",
    internship: "Internship", temporary: "Temporary", freelance: "Freelance",
    "Full-time": "Full Time", "Part-time": "Part Time",
  },
  workplaceType: {
    remote: "Remote", on_site: "On Site", hybrid: "Hybrid",
    onsite: "On Site", "on-site": "On Site",
  },
  seniorityLevel: {
    junior: "Junior", entry: "Entry Level", entry_level: "Entry Level",
    mid: "Mid Level", mid_level: "Mid Level", middle: "Mid Level",
    senior: "Senior", senior_level: "Senior Level",
    lead: "Lead", director: "Director", executive: "Executive",
    principal: "Principal", intern: "Intern",
  },
};

function toTitleCase(value: string) {
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function label(map: string, value: string) {
  if (!value) return "";
  const lower = value.toLowerCase().trim();
  return LABEL_MAPS[map]?.[value] ?? LABEL_MAPS[map]?.[lower] ?? toTitleCase(value);
}

function DetailItem({ label: l, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-xs mb-0.5" style={{ color: "var(--landing-text-muted)" }}>{l}</p>
      {/* A long email or job title breaks instead of running past the card on a phone. */}
      <p className="text-sm font-medium flex items-center gap-1.5 [overflow-wrap:anywhere]" style={{ color: "var(--landing-text)" }}>
        {children}
      </p>
    </div>
  );
}

function SectionHeading({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: "var(--landing-text)" }}>
      <Icon className="h-4 w-4 text-[var(--acc2)]" />
      {children}
    </h3>
  );
}

export default function ApplicationSubmitted() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SubmissionState | null;

  const submittedAt = new Date();
  const displayTimezone = getDisplayTimezone();
  const submittedDate = formatInstantInTimezone(
    submittedAt,
    displayTimezone,
    { year: "numeric", month: "long", day: "numeric" },
    "en-US"
  );
  const submittedTime = formatInstantTo12Hour(submittedAt, displayTimezone);

  if (!state) {
    return (
      <DcPage>
        <section
          className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
          style={{ background: "var(--landing-bg)" }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(var(--landing-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)",
              backgroundSize: "72px 72px",
              maskImage: "radial-gradient(ellipse 60% 50% at 50% 50%, black, transparent)",
              WebkitMaskImage: "radial-gradient(ellipse 60% 50% at 50% 50%, black, transparent)",
            }}
          />
          <div className="relative z-10 container py-16 md:py-24 text-center max-w-md mx-auto space-y-4">
            <div
              className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
            >
              <Briefcase className="h-8 w-8 text-[var(--acc2)]/60" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--landing-text)" }}>No submission data</h1>
            <p className="text-sm" style={{ color: "var(--landing-text-muted)" }}>
              This page is only accessible after submitting a job application.
            </p>
            <div className="pt-2">
              <Link to="/jobs" className="btn-electric px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2">
                Browse Jobs
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </DcPage>
    );
  }

  const { job, applicant } = state;

  return (
    <DcPage>
      <section className="relative min-h-screen py-12 md:py-16 overflow-hidden" style={{ background: "var(--landing-bg)" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(var(--landing-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(ellipse 70% 50% at 50% 10%, black, transparent)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 10%, black, transparent)",
          }}
        />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[450px] rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(var(--acc2-rgb),0.14) 0%, rgba(var(--pos-rgb),0.06) 45%, transparent 70%)",
            filter: "blur(80px)",
            opacity: "var(--landing-glow-mult)",
          }}
        />

        <div className="relative z-10 container">
          <div className="max-w-3xl mx-auto space-y-8">

            <div className="text-center space-y-4">
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full"
                style={{ background: "rgba(var(--acc2-rgb),0.1)", border: "1px solid rgba(var(--acc2-rgb),0.25)" }}
              >
                <CheckCircle2 className="h-8 w-8 text-[var(--acc2)]" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: "var(--landing-text)" }}>
                Application Submitted
              </h1>
              <p className="text-base max-w-lg mx-auto leading-relaxed" style={{ color: "var(--landing-text-muted)" }}>
                We've received your application and will review it shortly.
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden" style={glassCardStyle}>
              <div
                className="px-6 py-5"
                style={{ background: "var(--acc2)" }}
              >
                <h2 className="text-lg font-semibold" style={{ color: "var(--btnfg)" }}>
                  Application Details
                </h2>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <SectionHeading icon={Clock}>Submission Information</SectionHeading>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:ml-6">
                    <DetailItem label="Submitted On">{submittedDate}</DetailItem>
                    <DetailItem label="Time">{submittedTime}</DetailItem>
                    <div>
                      <p className="text-xs mb-0.5" style={{ color: "var(--landing-text-muted)" }}>Status</p>
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold mt-0.5"
                        style={{ background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.2)" }}
                      >
                        Under Review
                      </span>
                    </div>
                    <DetailItem label="Expected Response">Within 5 – 7 business days</DetailItem>
                  </div>
                </div>

                <div style={{ borderTop: "1px solid var(--landing-glass-border)" }} />

                <div>
                  <SectionHeading icon={Briefcase}>Position Applied For</SectionHeading>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:ml-6">
                    <DetailItem label="Job Title">{job.title}</DetailItem>
                    <DetailItem label="Company">AI2Aim Inc.</DetailItem>
                    <DetailItem label="Location">
                      <MapPin className="h-3.5 w-3.5" style={{ color: "var(--landing-text-dim)" }} />
                      {job.location}
                    </DetailItem>
                    <DetailItem label="Employment Type">{label("employmentType", job.employmentType)}</DetailItem>
                    <DetailItem label="Workplace">{label("workplaceType", job.workplaceType)}</DetailItem>
                    <DetailItem label="Level">{label("seniorityLevel", job.seniorityLevel)}</DetailItem>
                    {job.salaryRange && (
                      <DetailItem label="Salary Range">
                        <DollarSign className="h-3.5 w-3.5" style={{ color: "var(--landing-text-dim)" }} />
                        {job.salaryRange}
                      </DetailItem>
                    )}
                  </div>
                </div>

                <div style={{ borderTop: "1px solid var(--landing-glass-border)" }} />

                <div>
                  <SectionHeading icon={User}>Your Information</SectionHeading>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:ml-6">
                    <DetailItem label="Name">{applicant.firstName} {applicant.lastName}</DetailItem>
                    <DetailItem label="Email">
                      <Mail className="h-3.5 w-3.5" style={{ color: "var(--landing-text-dim)" }} />
                      {applicant.email}
                    </DetailItem>
                    {applicant.phone && (
                      <DetailItem label="Phone">
                        <Phone className="h-3.5 w-3.5" style={{ color: "var(--landing-text-dim)" }} />
                        {applicant.phone}
                      </DetailItem>
                    )}
                    {applicant.resumeFileName && (
                      <div>
                        <p className="text-xs mb-0.5" style={{ color: "var(--landing-text-muted)" }}>Resume</p>
                        <p className="text-sm font-medium flex items-center gap-1.5 text-[var(--acc2)] [overflow-wrap:anywhere]">
                          <FileText className="h-3.5 w-3.5" />
                          {applicant.resumeFileName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-6" style={glassCardStyle}>
              <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--landing-text)" }}>
                <ArrowRight className="h-5 w-5 text-[var(--acc2)]" />
                What Happens Next?
              </h3>
              <ul className="space-y-4">
                {[
                  {
                    step: 1,
                    title: "Application Review",
                    desc: "Our hiring team will review your application within 5 – 7 business days.",
                  },
                  {
                    step: 2,
                    title: "Email Notification",
                    desc: "You'll receive an email update about your application status.",
                  },
                  {
                    step: 3,
                    title: "Interview Process",
                    desc: "If selected, we'll contact you to schedule an interview.",
                  },
                ].map((item) => (
                  <li key={item.step} className="flex items-start gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: "var(--acc2)" }}
                    >
                      <span className="text-xs font-bold" style={{ color: "var(--btnfg)" }}>{item.step}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--landing-text)" }}>{item.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--landing-text-muted)" }}>{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/jobs")}
                className="px-6 py-3 rounded-lg font-semibold transition-colors"
                style={surfaceStyle}
              >
                Browse More Jobs
              </button>
              <button
                onClick={() => navigate(`/job/${jobId}`)}
                className="px-6 py-3 rounded-lg font-semibold transition-colors"
                style={surfaceStyle}
              >
                View Job Details
              </button>
              <button
                onClick={() => navigate("/")}
                className="btn-electric px-6 py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2"
              >
                <Home className="h-4 w-4" />
                Back to Home
              </button>
            </div>

            <p className="text-center text-sm" style={{ color: "var(--landing-text-muted)" }}>
              A confirmation email has been sent to{" "}
              <span className="font-medium [overflow-wrap:anywhere]" style={{ color: "var(--landing-text)" }}>{applicant.email}</span>
            </p>
          </div>
        </div>
      </section>
    </DcPage>
  );
}
